// Screenshot frame for docs pages. Rounded, hairline-bordered, flat surface.
// If the PNG is missing (Phase 3 lands the real captures later), onError swaps in
// a labeled dashed placeholder so the build/page never shows a broken-image icon.
import { useState } from 'react';

type ShotCallout = { n: number; label: string };

export function DocShot({ slug, shot, alt, caption, callouts, panel }: {
  slug: string;
  shot: string;
  alt: string;
  caption?: string;
  callouts?: ShotCallout[]; // numbered-callout list rendered under the frame
  panel?: boolean;          // narrow panel-surface framing hint
}) {
  const [failed, setFailed] = useState(false);
  // Cap the frame to the image's natural width so a small crop is shown at its
  // real size (crisp), never upscaled to fill the column and turn pixelated.
  const [nat, setNat] = useState<number | null>(null);
  const src = `/docs-assets/${slug}/${shot}.png`;

  return (
    <figure style={{ margin: '18px 0', maxWidth: nat ?? (panel ? 460 : '100%') }}>
      <div className="crw-docshot-frame" style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--line-2)', background: 'var(--surface-card)' }}>
        {failed ? (
          <div role="img" aria-label={alt} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
            minHeight: panel ? 180 : 220, padding: '28px 22px',
            border: '1px dashed var(--line-3)', borderRadius: 12,
            color: 'var(--fg-3)', fontSize: 12.5, lineHeight: 1.5,
          }}>
            <span style={{ maxWidth: 340 }}>
              <span style={{ display: 'block', marginBottom: 6, fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-4)' }}>Screenshot</span>
              {alt}
            </span>
          </div>
        ) : (
          <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} onLoad={(e) => setNat(e.currentTarget.naturalWidth)} style={{ display: 'block', width: '100%', height: 'auto' }} />
        )}
      </div>
      {callouts && callouts.length ? (
        <ol style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {callouts.map((c) => (
            <li key={c.n} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 13, color: 'var(--fg-2)' }}>
              <span style={{ flex: 'none', fontFamily: 'var(--font-mono)', fontSize: 10.5, fontWeight: 600, color: 'var(--signal-400)', border: '1px solid var(--line-2)', borderRadius: 5, padding: '1px 6px', background: 'var(--surface-raised)' }}>{c.n}</span>
              {c.label}
            </li>
          ))}
        </ol>
      ) : null}
      {caption ? (
        <figcaption style={{ marginTop: 8, fontSize: 12.5, color: 'var(--fg-3)', lineHeight: 1.5 }}>{caption}</figcaption>
      ) : null}
    </figure>
  );
}
